import React, { useEffect, useRef, useState } from "react";
import useKeypress from "../../../../../hooks/useKeypress";
import useOutsideClick from "../../../../../hooks/useOutsideClick";
import Button from "../../../Button";

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import Flex from "../../../../structure/Flex";
import Icon from "../../../Icon";

const getScrollTopFromInsideScroll = (
  outer: Element,
  inner: Element,
  scrollPosition: number
): number => {
  const elHeight = inner.scrollHeight;
  const scrollTop = outer.scrollTop;
  const viewport = scrollTop + outer.clientHeight;
  const elOffset = elHeight * scrollPosition;

  if (elOffset < scrollTop || elOffset + elHeight > viewport) {
    return elOffset;
  }
  return scrollTop;
};

interface MultiSelectProps {
  options?: Array<{ id: any; value: string; hint?: any }>;
  value: any;
  name: string;
  setValue: React.Dispatch<any>;
  placeholder?: string;
  isSortable?: boolean;
  hideSelected?: boolean;
  handleQuerySubmit?: any;
}

interface DraggableItemProps {
  id: string;
  tag: string;
  options: Array<{ id: any; value: string; hint?: any }>;
  removeOption: () => void;
}

const DraggableItem: React.FC<DraggableItemProps> = ({
  id,
  tag,
  options,
  removeOption,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id });

  return (
    <li
      ref={setNodeRef}
      className={`p-1 m-1 border border-dark leading-none text-sm rounded-lg inline-flex items-center ${
        isDragging ? "opacity-50" : ""
      }`}
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
      }}
      key={tag + id}
      {...listeners}
      {...attributes}
    >
      {options?.find((t) => `${t.id}` === tag)?.value}
      <button
        type="button"
        className="flex items-center justify-center ml-1"
        onClick={removeOption}
      >
        <Icon className="text-xs leading-none">close</Icon>
      </button>
    </li>
  );
};

const MultiSelect: React.FC<MultiSelectProps> = ({
  options = [],
  name,
  value,
  setValue,
  placeholder,
  isSortable,
  hideSelected,
  handleQuerySubmit,
}) => {
  const [query, setQuery] = useState<string>("");
  const [selected, setSelected] = useState<any[]>(() =>
    (value || []).map((v: any) => `${v}`)
  );

  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const [results, setResults] = useState<
    Array<{ id: any; value: string; hint?: any }>
  >([]);

  const multiSelectRef = useRef<HTMLDivElement | null>(null);
  const [scrollPosition, setScrollPosition] = useState(-1);

  const inputUpKey = useKeypress("ArrowUp", multiSelectRef, 300);
  const inputDownKey = useKeypress("ArrowDown", multiSelectRef, 300);
  const inputEscapeKey = useKeypress("Escape", multiSelectRef);

  useEffect(() => {
    const _results =
      query !== ""
        ? (options || []).filter(
            (option) =>
              selected.indexOf(`${option.id}`) === -1 &&
              option.value.toLowerCase().includes(query.toLowerCase())
          )
        : options || [];

    setResults(_results.map((r) => ({ ...r, id: `${r.id}` })));
  }, [options, query, selected]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setDropdownVisible(true);
  };

  //useEffect(() => {
  //  if (dropdownVisible) {
  //  } else {
  //    if (selected) {
  //      setQuery(
  //        options?.find((option) => option.id === selected)?.value || ""
  //      );
  //    } else {
  //      setQuery("");
  //      setSelected([]);
  //    }
  //    setResults(options || []);
  //  }
  //}, [dropdownVisible, options, selected]);

  //useEffect(() => {
  //  if (selected.length !== value.length) {
  //    setValue(selected);
  //  }
  //  setQuery(options?.find((option) => option.id === selected)?.value || "");
  //  // eslint-disable-next-line react-hooks/exhaustive-deps
  //}, [options, selected]);

  useEffect(() => {
    setValue(selected.map((s) => +s || s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    if (value && value.map((v: any) => v).join(" ") !== selected.join(" ")) {
      setSelected(value.map((v: any) => `${v}`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const toggleDropdown = () => {
    setDropdownVisible((v) => !v);
  };

  const addOption = (id: any) => {
    const optionExists = selected.findIndex((_option) => _option === `${id}`);
    if (optionExists !== -1) {
      removeOption(optionExists);
    } else {
      setSelected((t) => [...t, `${id}`]);
    }
  };

  const removeOption = (i: number) => {
    const newOptions = [...selected];
    newOptions.splice(i, 1);
    setSelected(newOptions);
  };

  useOutsideClick(multiSelectRef, () => setDropdownVisible(false));

  useEffect(() => {
    if (dropdownVisible && inputUpKey && results && results.length) {
      setScrollPosition((p) =>
        p === -1 ? results.length - 1 : Math.max(p - 1, 0)
      );
    }
  }, [dropdownVisible, inputUpKey, results]);

  useEffect(() => {
    if (dropdownVisible && inputDownKey && results && results.length) {
      setScrollPosition((p) => Math.min(p + 1, results.length - 1));
    }
  }, [dropdownVisible, inputDownKey, results]);

  useEffect(() => {
    dropdownVisible && inputEscapeKey && setDropdownVisible(false);
  }, [dropdownVisible, inputEscapeKey]);

  const handleInputEnterKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      dropdownVisible &&
        results &&
        results.length &&
        scrollPosition !== -1 &&
        addOption(results[scrollPosition].id);
      !dropdownVisible && toggleDropdown();
    }
  };

  useEffect(() => {
    const li = multiSelectRef.current
      ?.getElementsByClassName("multi_select__options")
      .item(scrollPosition);
    const ul = multiSelectRef.current?.querySelector(".multi__select");
    if (li && ul) {
      ul.scrollTop = getScrollTopFromInsideScroll(ul, li, scrollPosition);
    }
  }, [options, scrollPosition]);

  const onDragEnd = (event: { active: any; over: any }) => {
    const { active, over } = event;
    if (active.id && over && active.id !== over.id) {
      setSelected((selected) => {
        const oldIndex = selected.indexOf(active.id);
        const newIndex = selected.indexOf(over.id);
        return arrayMove(selected, oldIndex, newIndex);
      });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 3 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const renderSelectedItems = () => {
    if (!hideSelected) {
      if (isSortable) {
        return (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={selected} strategy={rectSortingStrategy}>
              {selected.map((tag, i) => (
                <DraggableItem
                  key={tag}
                  id={tag}
                  tag={tag}
                  options={options}
                  removeOption={() => removeOption(i)}
                />
              ))}
            </SortableContext>
          </DndContext>
        );
      } else {
        return (
          <>
            {selected.map((tag, i) => (
              <li
                key={tag}
                className="inline-flex items-center p-1 m-1 text-sm leading-none border rounded-lg border-dark"
              >
                {options?.find((t) => `${t.id}` === tag)?.value}
                <button
                  type="button"
                  className="flex items-center justify-center ml-1"
                  onClick={() => {
                    removeOption(i);
                  }}
                >
                  <Icon className="text-xs leading-none">close</Icon>
                </button>
              </li>
            ))}
          </>
        );
      }
    }
  };

  return (
    <div
      className="relative flex flex-col items-center w-full"
      ref={multiSelectRef}
      onKeyDown={handleInputEnterKeyDown}
    >
      <div className="relative w-full z-1">
        <ul
          className={`flex w-full flex-wrap border ${
            dropdownVisible ? "rounded-t-lg" : "rounded-lg"
          } p-2 bg-white`}
        >
          {renderSelectedItems()}
          <li className="flex flex-grow input-tag__tags__input">
            <input
              className="z-50 flex-grow p-1 focus:outline-none"
              type="text"
              placeholder={placeholder || "Select ..."}
              value={query}
              onChange={handleChange}
              onClick={() => toggleDropdown()}
            />
            <span className="flex items-center justify-between text-xs font-medium text-gray-500">
              <Button
                type="icon no-style"
                icon="done_all"
                className="mx-1"
                onClick={() => setSelected(options.map((o) => `${o.id}`))}
              ></Button>
              <Button
                type="icon no-style"
                icon="cancel"
                className="mx-1"
                onClick={() => setSelected([])}
              ></Button>
            </span>
          </li>
        </ul>
      </div>

      {dropdownVisible && (
        <ul
          className="absolute left-0 z-20 w-full p-2 overflow-y-auto bg-white border rounded-b-lg shadow-md multi__select top-full"
          style={{ maxHeight: "20rem" }}
        >
          {results && results.length > 0 ? (
            results.map((option, index) => (
              <li
                key={`${option.id}`}
                className={`multi_select__options w-full p-2 mt-1 cursor-pointer flex flex-col hover:bg-accent rounded-lg ${
                  selected.indexOf(`${option.id}`) !== -1 && "bg-accent"
                } ${
                  scrollPosition === index &&
                  "bg-accent border border-gray-500 shadow-sm"
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addOption(option.id);
                }}
              >
                <span>{option.value}</span>
                {option.hint && (
                  <span className="mt-1 text-sm font-medium text-subtitle">
                    {option.hint}
                  </span>
                )}
              </li>
            ))
          ) : handleQuerySubmit ? (
            <Flex justify="center" items="center">
              <Button
                type="outlined"
                className="m-1"
                onClick={() => handleQuerySubmit(query, setQuery)}
              >
                Create
              </Button>
            </Flex>
          ) : (
            <div className="w-full p-2 text-sm font-medium text-center pointer-events-none text-subtitle">
              No options
            </div>
          )}
        </ul>
      )}
    </div>
  );
};

export default MultiSelect;
